import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { CSSProperties, VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Link, Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useCTAButtonLink } from "hooks";
import { FullArrow } from "icons";
import { getImageFormat } from "lib/utils";
import { PartnerInfoContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: PartnerInfoContent;
}

const PartnerInfo: VFC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      logoModal: {
        data: { logoModal },
      },
      description,
      stepsHeader,
      features4Steps,
      ctaButtonLabel,
      learnMoreButtonLabel,
      learnMoreButtonLink,
      learnMoreButtonTypes,
      privacyPolicy,
      premiumPartnerTitle,
      premiumPartnerDescription,
      otherPartnerDescription,
    },
  }) => {
    return (
      <StyledPartnerInfo
        className={cn(className, {
          PartnerInfo_isIntegrated: !!privacyPolicy,
        })}
        style={
          {
            "--logo-aspect-ratio": logoModal.width / logoModal.height,
          } as CSSProperties
        }
      >
        <div className="PartnerInfo__header ]">
          <LegacyImage
            src={logoModal.src}
            alt={logoModal.alt}
            transform={{ format: getImageFormat(logoModal.ext) }}
            layout="fill"
            objectFit="contain"
            className="PartnerInfo__logo ]"
          />
          <Markdown className="PartnerInfo__headerDescription Color_primary Font_24_32_450 md:Font_18_26_450">
            {description}
          </Markdown>
        </div>

        <div className="PartnerInfo__body">
          <h3 className="PartnerInfo__stepsHeader Color_primary Font_24_32_600">{stepsHeader}</h3>
          <ul className="PartnerInfo__steps">
            {features4Steps.map(({ description }, index) => (
              <li key={description} className="PartnerInfo__step">
                <div className="PartnerInfo__stepNumber Color_primary Font_18_26_600">{index + 1}</div>
                <Markdown className="PartnerInfo__stepDescription Color_primary Font_18_26_600">{description}</Markdown>
              </li>
            ))}
          </ul>
          {otherPartnerDescription && (
            <Markdown className="PartnerInfo__otherPartnersDescription Font_18_26_450">
              {otherPartnerDescription}
            </Markdown>
          )}
          <div className="PartnerInfo__buttons">
            <PipelineRoutingLinkButton
              href={useCTAButtonLink(learnMoreButtonTypes)}
              className="PartnerInfo__ctaButton"
              rightIcon={<FullArrow />}
              sourceIdentifier="website"
            >
              {ctaButtonLabel}
            </PipelineRoutingLinkButton>
            {privacyPolicy && learnMoreButtonLink && (
              <Link href={learnMoreButtonLink} className="PartnerInfo__learnMoreButton Color_secondary Font_18_26_600">
                {learnMoreButtonLabel}
              </Link>
            )}
          </div>
          {privacyPolicy && (
            <Markdown className="PartnerInfo__privacyPolicy Color_tertiary Font_14_20_450 md:Font_12_16_450">
              {privacyPolicy}
            </Markdown>
          )}
          {learnMoreButtonLink && (
            <Link
              href={learnMoreButtonLink}
              className="PartnerInfo__learnMoreButtonBottom Color_secondary Font_18_26_450 md:Font_18_26_600"
            >
              {learnMoreButtonLabel}
            </Link>
          )}
          {premiumPartnerTitle && premiumPartnerDescription && (
            <div className="PartnerInfo__additionalSection">
              <p className="PartnerInfo__premiumTitle Font_16_22_700">{premiumPartnerTitle}</p>
              <Markdown className="PartnerInfo__premiumDescription Font_16_22_450">
                {premiumPartnerDescription}
              </Markdown>
            </div>
          )}
        </div>
      </StyledPartnerInfo>
    );
  },
  {}
);

const StyledPartnerInfo = styled.div`
  width: 580px;
  max-width: 100%;

  &.PartnerInfo_isIntegrated {
    width: 664px;

    .PartnerInfo__learnMoreButtonBottom {
      display: none;
    }
  }

  .PartnerInfo__header {
    padding: 40px;
    background-color: ${({ theme }) => theme.colors.fog["10"]};
  }

  .PartnerInfo__logo {
    position: relative;
    width: calc(50px * var(--logo-aspect-ratio));
    max-width: 100%;
    height: 50px;
  }

  .PartnerInfo__headerDescription {
    margin-top: 32px;
  }

  .PartnerInfo__body {
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${({ theme }) => theme.colors.fog["00"]};
  }

  .PartnerInfo__steps {
    margin-top: 24px;
  }

  .PartnerInfo__step {
    display: flex;
    align-items: baseline;
  }

  .PartnerInfo__step:not(:first-child) {
    margin-top: 16px;
  }

  .PartnerInfo__stepNumber {
    width: 64px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["80"]};
    border-radius: 20px;
  }

  .PartnerInfo__stepDescription {
    margin-left: 16px;
  }

  .PartnerInfo__otherPartnersDescription {
    margin-top: 48px;
  }

  .PartnerInfo__buttons {
    display: flex;
    align-items: center;
    margin-top: 48px;
  }

  .PartnerInfo__ctaButton {
    align-self: flex-start;
  }

  .PartnerInfo__learnMoreButton {
    margin-left: 40px;
    text-decoration: underline;
    border-radius: 4px;

    :hover {
      text-decoration: none;
    }
  }

  .PartnerInfo__privacyPolicy {
    margin-top: 24px;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .PartnerInfo__learnMoreButtonBottom {
    margin-top: 24px;
    text-decoration: underline;

    :hover {
      text-decoration: none;
    }
  }

  .PartnerInfo__additionalSection {
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
    padding: 24px;
    background: ${({ theme }) => theme.colors.fog["05"]};
    border: 1px solid #c3c2c1;
    border-radius: 8px;
  }

  .PartnerInfo__premiumDescription {
    max-width: 302px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;

    &.PartnerInfo_isIntegrated {
      width: 100%;

      .PartnerInfo__learnMoreButtonBottom {
        display: block;
      }
    }

    .PartnerInfo__headerDescription {
      margin-top: 16px;
      max-width: 450px;
    }

    .PartnerInfo__stepsHeader,
    .PartnerInfo__steps {
      display: none;
    }

    .PartnerInfo__header,
    .PartnerInfo__body {
      background-color: ${({ theme }) => theme.colors.fog["10"]};
    }

    .PartnerInfo__logo {
      width: calc(40px * var(--logo-aspect-ratio));
      height: 40px;
    }

    .PartnerInfo__body {
      padding: 0 24px 24px;
    }

    .PartnerInfo__header {
      padding: 24px 24px 0;
    }

    .PartnerInfo__otherPartnersDescription {
      margin-top: 32px;
    }

    .PartnerInfo__buttons {
      margin-top: 60px;
    }

    .PartnerInfo__learnMoreButton {
      display: none;
    }

    .PartnerInfo__privacyPolicy {
      max-width: 450px;
    }

    .PartnerInfo__additionalSection {
      justify-content: flex-start;
    }

    .PartnerInfo__premiumTitle {
      min-width: 160px;
    }

    .PartnerInfo__premiumDescription {
      margin-left: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .PartnerInfo__additionalSection {
      flex-direction: column;
    }

    .PartnerInfo__premiumDescription {
      margin-top: 8px;
      margin-left: 0;
    }
  }
`;

gql`
  fragment PartnerInfoContent on ComponentNestedFeatures4Partners {
    logoModal {
      data {
        logoModal: attributes {
          src: hash
          alt: alternativeText
          width
          height
          ext
        }
      }
    }
    description
    stepsHeader
    features4Steps {
      description
    }
    ctaButtonLabel
    learnMoreButtonLabel
    learnMoreButtonLink
    learnMoreButtonTypes
    privacyPolicy
    premiumPartnerTitle
    premiumPartnerDescription
    otherPartnerDescription
  }
`;

export { PartnerInfo };
